import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast';

/** Last path segment of a URL, decoded, without the query string. */
export function filenameFromUrl(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  const last = path.split('/').pop() || '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function filenameFromDisposition(disposition: string): string {
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
    disposition
  );
  const raw = (match?.[1] || match?.[2] || '').trim();
  if (!raw) return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Saves a stored file to the user's machine (ZLAYT-1445).
 *
 * Download controls used to window.open() the storage URL in a new tab, the
 * pattern QA D-21 reported. The file is fetched and saved from a blob under
 * the server's content-disposition name, else the given name, else the URL's
 * last segment. Requests to our own API carry the bearer token; presigned
 * storage URLs are fetched without credentials.
 *
 * An HTTP error is reported rather than saved as the file. Only when the
 * fetch itself fails (offline, or the storage CORS rule does not admit the
 * app, see ZLAYT-1444) does it fall back to opening the URL, so the user can
 * still get the file.
 */
export async function downloadFile(url: string, filename?: string) {
  if (!url) return;
  const apiBase = (import.meta as any).env?.VITE_APP_API_URL || '';
  const headers: Record<string, string> = {};
  if (apiBase && url.startsWith(apiBase)) {
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || 'null');
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // No usable token; the request goes out without it.
    }
  }

  let res: Response;
  try {
    res = await fetch(url, { headers, credentials: 'omit' });
  } catch (e) {
    console.error('[downloadFile] fetch failed, opening the file instead', e);
    window.open(url, '_blank', 'noopener');
    return;
  }

  if (!res.ok) {
    console.error(`[downloadFile] HTTP ${res.status} for download`);
    toast(({ id }) => (
      <Toast
        id={id}
        type="error"
        title="Download failed"
        text={`This file could not be downloaded: storage refused the request (HTTP ${res.status}).`}
      />
    ));
    return;
  }

  const blob = await res.blob();
  const name =
    filenameFromDisposition(res.headers.get('content-disposition') || '') ||
    filename ||
    filenameFromUrl(url) ||
    'document';

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
